import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  IconButton
} from '@mui/material';
import { Close, Star, RateReview } from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';

interface ReviewDescriptionModalProps {
  open: boolean;
  onClose: () => void;
  rating: number;
  description: string;
}

const ReviewDescriptionModal: React.FC<ReviewDescriptionModalProps> = ({
  open,
  onClose,
  rating,
  description
}) => {
  const { t } = useTranslation();
  const theme = useTheme();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3
        }
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          fontWeight: 600,
          fontSize: 20,
          color: theme.palette.mode === 'light' ? '#424242' : '#FFFFFF',
          pb: 2
        }}
      >
        <Box display="flex" alignItems="center" gap={1}>
          <RateReview sx={{ fontSize: 24, color: '#5073F8' }} />
          {t('trips.reviewModal.title')}
        </Box>
        <IconButton onClick={onClose} size="small">
          <Close />
        </IconButton>
      </DialogTitle>
      
      <DialogContent>
        <Box
          sx={{
            mb: 3,
            p: 2,
            backgroundColor: theme.palette.mode === 'light' ? '#F5F5F5' : '#2C2C2C',
            borderRadius: 2,
            display: 'flex',
            alignItems: 'center',
            gap: 1
          }}
        >
          <Typography variant="body2" color="textSecondary">
            {t('trips.reviewModal.rating')}:
          </Typography>
          <Box display="flex" alignItems="center" gap={0.5}>
            <Star sx={{ fontSize: 20, color: '#4ECDC4' }} />
            <Typography variant="body1" fontWeight="bold">
              {rating}
            </Typography>
          </Box>
        </Box>

        <Box
          sx={{
            p: 2,
            backgroundColor: theme.palette.mode === 'light' ? '#FFFFFF' : '#1E1E1E',
            border: `1px solid ${theme.palette.mode === 'light' ? '#E0E0E0' : '#424242'}`,
            borderRadius: 2,
            minHeight: 100
          }}
        >
          <Typography
            variant="body2"
            sx={{
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              color: theme.palette.mode === 'light' ? '#424242' : '#E0E0E0'
            }}
          >
            {description}
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button
          onClick={onClose}
          variant="contained"
          sx={{
            backgroundColor: theme.palette.mode === 'light' ? '#424242' : '#4ECDC4',
            color: theme.palette.mode === 'light' ? '#FFF' : '#000',
            '&:hover': {
              backgroundColor: theme.palette.mode === 'light' ? '#616161' : '#3BA99F'
            }
          }}
        >
          {t('trips.reviewModal.close')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ReviewDescriptionModal;
